import { BottomDrawerModalAnimated } from "@/components/shared/bottom-drawer-modal-animated";
import { PackageDepartmentEnum } from "@/__generated__/graphql";
import { useRoute } from "@/components/shared/router";
import { Typography } from "@/components/shared/typography";
import type { VisitDetailData } from "@/components/shared/visits/visit-detail";
import { Button, View } from "@/components/ui";
import { ReviewCard } from "@/components/shared/review-card/review-card";
import { getServiceNameFromDepartment } from "@/lib/booking-lib";
import { formatClientNameHide } from "@/lib/helpers/string";
import { formatServiceDateTime } from "@/lib/helpers/date";
import { bookingRoutes } from "@/components/hooks/use-booking-route";
import { packageServiceTypeLabel } from "@/lib/service/helpers";
import { IconButton } from "@/components/ui/icon-button";
import { useState } from "react";
import { RateVisitImageCarousel } from "./rate-visit-image-carousel";
import { useVisitDetailStore } from "@/store/visits/visitDetail";

export function RateVisitSubmitted({
  visitData,
}: {
  visitData: VisitDetailData;
}) {
  const { push } = useRoute();
  const [open, setOpen] = useState(true);
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const closeRateVisitModal = useVisitDetailStore((state) => {
    return state.closeRateVisitModal;
  });

  const handleRebook = (department: PackageDepartmentEnum) => {
    const serviceName = getServiceNameFromDepartment(department);
    push(bookingRoutes[serviceName][0]);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
    setTimeout(() => {
      closeRateVisitModal();
    }, 300);
  };

  const handleImageClick = () => {
    setOpen(false);
    setTimeout(() => {
      setImageModalOpen(true);
    }, 300);
  };

  return (
    <>
      <BottomDrawerModalAnimated
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            handleClose();
          }
        }}
        title={
          <View className="flex flex-row items-center justify-between gap-2 border-b border-border pb-4">
            <Typography variant="label-lg">Your Review</Typography>
            <IconButton
              variant="ghost"
              color="foreground"
              size="md"
              iconName="x"
              onClick={handleClose}
              className="native:hidden p-0"
            />
          </View>
        }
      >
        <ReviewCard
          clientName={formatClientNameHide(visitData.clientName)}
          rating={visitData.rating}
          date={visitData.serviceDate}
          description={visitData.ratingComment}
          images={visitData.reviewImageUrls}
          onClick={handleImageClick}
        />
        <View className="my-4 flex flex-row items-center justify-between gap-2 rounded-lg border border-border px-4 py-3">
          <View className="flex flex-col gap-1">
            <Typography variant="body-sm">
              {packageServiceTypeLabel(
                visitData.serviceType,
                visitData.department,
              )}
            </Typography>
            <Typography variant="caption-md" color="foreground-intermediate">
              {formatServiceDateTime(
                visitData.serviceDate,
                visitData.startTime,
                visitData.endTime,
              )}
            </Typography>
          </View>
          <Button
            children="Rebook"
            variant="primary"
            color="CTA"
            size="md"
            onClick={() => handleRebook(visitData.department)}
          />
        </View>
      </BottomDrawerModalAnimated>
      <RateVisitImageCarousel
        visitData={visitData}
        open={imageModalOpen}
        onOpenChange={(o) => {
          if (!o) {
            setImageModalOpen(false);
            setTimeout(() => {
              setOpen(true);
            }, 300);
          }
        }}
      />
    </>
  );
}
